import { forwardRef, type ReactNode } from 'react';
import { FileViewer } from '@/components/common/media-content/file-viewer';
import VideoViewer from '@/components/common/media-content/video-viewer';
import { SafeImage } from '@/components/common/safe-image';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import cn from '@/utils/merge-class-names';
import AudioPlayer from './audio-player';
import ImageZoomPan, { type ImageZoomActions } from './image-zoom-pan';

interface Props {
  type: ConversationMessageContentType;
  src: string;
  wrapperClassName?: string;
  className?: string;
  isInView?: boolean;
  autoPlay?: boolean;
  playBackTime?: number;
  onImageZoom?: (isZooming: boolean) => void;
}

const MediaContent = forwardRef<ImageZoomActions | HTMLVideoElement, Props>((props, ref) => {
  const {
    type,
    src,
    className,
    isInView = true,
    autoPlay = false,
    onImageZoom,
    wrapperClassName,
    playBackTime,
  } = props;

  let content: ReactNode;
  switch (type) {
    case ConversationMessageContentType.Image:
      content = (
        <ImageZoomPan
          ref={ref as React.RefObject<ImageZoomActions>}
          onImageZoom={onImageZoom}
          src={src}
          isInView={isInView}
          className={className}>
          <SafeImage
            displayType='img'
            src={src}
            isInView={isInView}
            className={cn(
              'aspect-auto h-auto max-h-[calc(100dvh-8rem)] mode-embedded:max-h-[calc(100dvh-14rem)] w-full desktop:object-contain desktop:bg-black',
              className,
            )}
            alt='media content'
          />
        </ImageZoomPan>
      );
      break;
    case ConversationMessageContentType.Video:
      content = (
        <VideoViewer
          playBackTime={playBackTime}
          ref={ref as React.RefObject<HTMLVideoElement>}
          controls
          autoPlay={autoPlay}
          src={src}
          isInView={isInView}
          preload='metadata'
          wrapperClassName='max-h-[calc(100dvh-8rem)] mode-embedded:max-h-[calc(100dvh-14rem)]'
          className={cn('size-full object-contain', className)}
        />
      );
      break;
    case ConversationMessageContentType.Audio:
      content = (
        <AudioPlayer
          barCount={40}
          className={cn('rounded-box bg-metal-50 gap-8 px-6', className)}
          src={src}
          compact={true}
        />
      );
      break;
    case ConversationMessageContentType.File:
      content = (
        <FileViewer
          className={className}
          src={src}
        />
      );
      break;
    default:
      content = (
        <div
          className={cn(
            'break-all text-sm w-full max-h-[calc(100dvh-8rem)] overflow-y-auto overflow-x-hidden whitespace-pre-wrap',
            className,
          )}>
          {src}
        </div>
      );
      break;
  }

  return (
    <div
      className={cn('relative flex flex-1 size-full items-center justify-center touch-pan-x', wrapperClassName, {
        'items-stretch justify-start': type === ConversationMessageContentType.Text,
      })}>
      <span className='loading text-primary loading-spinner absolute left-1/2 top-1/2 -translate-x-1/2 loading-lg -translate-y-1/2 -z-10' />
      {content}
    </div>
  );
});

MediaContent.displayName = 'MediaContent';

export default MediaContent;
